import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  Search, Save, X, Pencil, Undo2, Info, TrendingUp, TrendingDown, Minus,
} from "lucide-react";

const MONTH_LABEL = (ym) => {
  if (!ym || ym.length < 7) return ym;
  const [y, m] = ym.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
};

function trendVs(cell, prev) {
  if (!cell || cell.rate == null || !prev || prev.rate == null) return null;
  const diff = cell.rate - prev.rate;
  if (Math.abs(diff) < 0.01) return { kind: "flat", pct: 0 };
  const pct = (diff / prev.rate) * 100;
  return { kind: diff > 0 ? "up" : "down", pct };
}

function TrendPill({ trend }) {
  if (!trend) return null;
  if (trend.kind === "flat") {
    return <span className="inline-flex items-center gap-0.5 text-[10px] text-gray-400"><Minus size={10} /> flat</span>;
  }
  const up = trend.kind === "up";
  const cls = up ? "text-[#B91C1C]" : "text-[#15803D]";
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold ${cls}`}>
      <Icon size={10} /> {up ? "+" : ""}{trend.pct.toFixed(1)}%
    </span>
  );
}

function Cell({ cell }) {
  if (!cell || cell.rate == null) {
    return <span className="text-gray-300 text-xs">—</span>;
  }
  const rate = Number(cell.rate).toFixed(2);
  if (cell.source === "override") {
    return (
      <div className="leading-tight">
        <div className="data-num font-bold text-[#B45309]">₹ {rate}</div>
        <div className="text-[9px] uppercase tracking-wider text-[#B45309] font-bold">override</div>
      </div>
    );
  }
  if (cell.source === "ledger") {
    return (
      <div className="leading-tight">
        <div className="data-num font-bold">₹ {rate}</div>
        {cell.kg != null && (
          <div className="text-[10px] text-gray-500 data-num">{Number(cell.kg).toLocaleString()} kg</div>
        )}
      </div>
    );
  }
  // previous fallback
  return (
    <div className="leading-tight">
      <div className="data-num text-gray-500 italic">₹ {rate}</div>
      <div className="text-[9px] uppercase tracking-wider text-gray-400">carry-over</div>
    </div>
  );
}

function EditModal({ row, currentMonth, onClose, onSaved }) {
  const initial = row.cells?.[currentMonth];
  const [rate, setRate] = useState(initial?.rate != null ? String(initial.rate) : "");
  const [note, setNote] = useState(initial?.note || "");
  const [busy, setBusy] = useState(false);
  const isOverride = initial?.source === "override";

  const save = async (e) => {
    e.preventDefault();
    const val = Number(rate);
    if (!val || val <= 0) { toast.error("Rate must be > 0"); return; }
    setBusy(true);
    try {
      await api.put(`/rate-card/${row.party_id}`, { rate_per_kg: val, note });
      toast.success("Rate saved for this month");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const clearOverride = async () => {
    if (!window.confirm("Remove manual override and fall back to ledger rate?")) return;
    setBusy(true);
    try {
      await api.delete(`/rate-card/${row.party_id}`);
      toast.success("Override cleared");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="rate-edit-modal">
      <form onSubmit={save} className="bg-white rounded-sm max-w-md w-full">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <div className="small-label text-gray-500">Rate override · {MONTH_LABEL(currentMonth)}</div>
            <div className="font-heading font-black text-lg">{row.party_name}</div>
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-gray-100 rounded-sm"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="small-label block mb-2">Rate (₹ / kg) *</label>
            <input type="number" step="0.01" min="0" required
              value={rate} onChange={(e) => setRate(e.target.value)}
              className="input-lg" autoFocus data-testid="rate-input" />
          </div>
          <div>
            <label className="small-label block mb-2">Note (optional)</label>
            <input value={note} onChange={(e) => setNote(e.target.value)}
              className="input-lg" placeholder="reason for the manual rate"
              data-testid="rate-note-input" />
          </div>
          <div className="flex items-start gap-2 text-[11px] text-gray-500 bg-gray-50 border border-gray-200 rounded-sm p-2">
            <Info size={13} className="text-gray-400 shrink-0 mt-0.5" />
            <span>
              Overrides <b>do not affect sale entries</b>. This card only tracks the reference rate you plan to bill this customer for the current month.
              If you don&apos;t override, the current month keeps showing the previous bill&apos;s rate.
            </span>
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 px-5 py-4 border-t bg-gray-50">
          <div>
            {isOverride && (
              <button type="button" onClick={clearOverride} disabled={busy}
                className="text-xs font-semibold text-[#B91C1C] hover:text-black inline-flex items-center gap-1"
                data-testid="rate-clear-override">
                <Undo2 size={13} /> Clear override
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className="h-9 px-4 border border-gray-300 rounded-sm font-semibold text-sm">Cancel</button>
            <button type="submit" disabled={busy}
              className="btn-primary h-9 px-4 flex items-center gap-1.5"
              data-testid="rate-save-btn">
              <Save size={14} /> {busy ? "Saving…" : "Save rate"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default function RateCard() {
  const { user } = useAuth();
  const isReadOnly = user?.role === "stock_viewer" || user?.role === "sales";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/rate-card");
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const months = data?.months || [];
  const currentMonth = data?.current_month || "";

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data?.rows || [];
    return (data?.rows || []).filter((r) =>
      (r.party_name || "").toLowerCase().includes(q) ||
      (r.phone || "").toLowerCase().includes(q)
    );
  }, [data, search]);

  return (
    <div className="space-y-5" data-testid="rate-card-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="small-label text-gray-500">Rate Card</div>
          <h2 className="font-heading font-black text-2xl">₹ / kg by customer & month</h2>
          <div className="text-xs text-gray-500 mt-1 max-w-2xl">
            Effective ₹/kg is derived from your sales ledger. Prior months are read-only.
            Set the <b>current month</b> rate manually if you plan to change what you bill —
            if not set, the previous month&apos;s rate is shown as a reminder. This card does <b>not</b> alter any sale entry.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search customer…"
              className="input-lg pl-9 w-56"
              data-testid="rate-search" />
          </div>
        </div>
      </div>

      <div className="card-flat">
        <div className="table-wrap">
          <table className="table-dense w-full min-w-[900px]">
            <thead>
              <tr>
                <th className="text-left sticky left-0 bg-white z-10">Customer</th>
                {months.map((m) => (
                  <th key={m} className="text-center min-w-[110px]"
                      data-testid={`rc-col-${m}`}>
                    <div className="uppercase tracking-wider text-[11px] font-black">{MONTH_LABEL(m)}</div>
                    {m === currentMonth && (
                      <div className="text-[9px] font-bold text-[#B45309] tracking-wider">CURRENT</div>
                    )}
                  </th>
                ))}
                {!isReadOnly && <th className="text-right">Action</th>}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={months.length + 2} className="text-center py-12 text-gray-400">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={months.length + 2} className="text-center py-12 text-gray-400">No customer sales in the last 5 months.</td></tr>
              )}
              {!loading && rows.map((r) => (
                <tr key={r.party_id} data-testid={`rc-row-${r.party_id}`}>
                  <td className="sticky left-0 bg-white">
                    <Link to={`/parties/${r.party_id}`} className="font-semibold hover:underline">{r.party_name}</Link>
                    {r.phone && <div className="text-[10px] text-gray-500">{r.phone}</div>}
                  </td>
                  {months.map((m, idx) => {
                    const cell = r.cells?.[m];
                    const prev = idx > 0 ? r.cells?.[months[idx - 1]] : null;
                    const trend = trendVs(cell, prev);
                    const isCurrent = m === currentMonth;
                    return (
                      <td key={m}
                        className={`text-center align-middle ${isCurrent ? "bg-[#FEF3C7]/40" : ""}`}
                        data-testid={`rc-cell-${r.party_id}-${m}`}>
                        <div className="flex flex-col items-center gap-0.5">
                          <Cell cell={cell} />
                          <TrendPill trend={trend} />
                        </div>
                      </td>
                    );
                  })}
                  {!isReadOnly && (
                    <td className="text-right">
                      <button
                        onClick={() => setEditing(r)}
                        className="text-xs font-semibold text-gray-600 hover:text-black inline-flex items-center gap-1"
                        data-testid={`rc-edit-${r.party_id}`}
                      >
                        <Pencil size={12} /> Edit current
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center gap-4 text-[11px] text-gray-500 flex-wrap">
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded-sm bg-black"></span> Ledger avg (₹ / kg)
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded-sm bg-[#B45309]"></span> Manual override (current month)
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded-sm bg-gray-300"></span> Carry-over from previous bill
        </span>
      </div>

      {editing && (
        <EditModal
          row={editing}
          currentMonth={currentMonth}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}
